// ============================================
// tenantLocalTime — typed clock times are COMPANY time, never phone time
// ============================================
// Side-effect module (UMD): attaches `window.TenantLocalTime`. app.js imports it
// once, before clockShiftGuard and every view module; views read it through
// `window.TenantLocalTime` at CALL time — never a cross-`?v=` ES import
// (Pitfall #1). Node unit tests require() it: tests/tenant-local-time.unit.cjs.
//
// WHY (clock-dst-all, 2026-10-03). Every manual clock time box (clock-out sheet,
// Workforce edit, board forgot-clock-out, ticket equipment / downtime / standby
// rows, work start/stop) turned the typed "2026-11-01T01:30" into an instant with
// `new Date(str)` — the PHONE's zone. So:
//   (a) a phone in Pacific editing a Chicago company's shift saved every time
//       two hours off, and
//   (b) on the fall-back night the browser silently took the FIRST 1:30 AM; a
//       finish time in the repeated hour lost an hour of pay.
// This module is the one place a wall time becomes an instant:
//   - the zone is the COMPANY's: settings.timezone → tenant.timezone →
//     America/Chicago (unset), the same resolution the server uses
//     (functions/src/services/fieldTicketDocument.js resolveTenantTimeZone);
//   - a time that happened twice is NEVER silently picked — read() says
//     'ambiguous' and resolveAll() asks the person which one (with the hours each
//     gives) before a save;
//   - a time that never happened (spring-forward gap) is refused;
//   - a zone that cannot be read (unknown IANA name, or the company settings
//     never loaded) is refused — never a fall back to the phone.
// clockShiftGuard.js (the stuck-shift late close) delegates its zone math here.
//
// What a box remembers (COD 1.15.2009 — no cross-row / time-cache inheritance):
//  - A KEYED single field (clock in, clock out, work start…) has ONE slot.
//    formatLocal(instant, {key}) overwrites it every time the box is filled, so
//    saving that box unchanged keeps the stored instant. A pick made in the
//    picker is written to the slot for the save that follows, but a LATER
//    resolveAll never reuses a pick (only a filled-in value) unless the caller
//    says it is the same save (reusePicks) — a new entry typed with the same
//    minute is asked again.
//  - A ticket ROW (equipment / downtime / standby) remembers on the ROW OBJECT
//    itself (WeakMap: never serialized, never shared): stampRow() when the row's
//    time is set from a known instant, and the row's own pick. Each row's start
//    and end are asked separately; one row's answer is never another's.
//  - formatLocal with no key and no row remembers NOTHING.
(function (root) {
  'use strict';

  const DEFAULT_TZ = 'America/Chicago';
  const HINT_TTL_MS = 2 * 60 * 60 * 1000;
  const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

  let _override = null;          // setZone() — a page that learns the zone itself
  const _hints = new Map();      // key -> { str, ms, kind:'render'|'pick', at }
  const _rows = new WeakMap();   // row object -> { [field]: { str, ms } }

  const ZONE_BAD_MSG = "Couldn't read the company time zone, so this time can't be saved safely. Ask the office to check Settings → Company time zone.";
  const ZONE_NOT_LOADED_MSG = "The company settings haven't loaded, so this time can't be saved safely. Refresh and try again.";

  // TASK-1507 (COD r1 Code Reviewer): payroll asks once per clock entry, so a
  // zone name that already validated is remembered instead of building a new
  // Intl.DateTimeFormat every call.
  const _okZones = new Set();
  let _okCtor = null;
  function _validZone(tz) {
    // A swapped-out Intl (tests: "Intl throwing → refuse") invalidates both caches.
    if (_okCtor !== Intl.DateTimeFormat) { _okZones.clear(); _fmts.clear(); _okCtor = Intl.DateTimeFormat; }
    if (_okZones.has(tz)) return true;
    try { new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date(0)); } catch (_) { return false; }
    if (_okZones.size > 50) _okZones.clear();
    _okZones.add(tz);
    return true;
  }

  function _nonEmpty(o) { return !!o && typeof o === 'object' && Object.keys(o).length > 0; }

  // { ok:true, tz } | { ok:false, code:'zone'|'not-loaded', error }
  function resolveZone(opts) {
    let raw = opts && opts.tz;
    let loaded = !!raw;
    if (!raw && _override) { raw = _override; loaded = true; }
    if (!raw) {
      const st = (root && root.AppState) || {};
      const s = st.settings || {};
      const t = st.tenantData || {};
      raw = (typeof s.timezone === 'string' && s.timezone.trim())
        || (t.settings && typeof t.settings.timezone === 'string' && t.settings.timezone.trim())
        || (typeof t.timezone === 'string' && t.timezone.trim())
        || '';
      loaded = !!raw || _nonEmpty(s) || _nonEmpty(t);
    }
    raw = String(raw || '').trim();
    if (!raw) {
      if (!loaded && !(opts && opts.allowDefault)) return { ok: false, code: 'not-loaded', error: ZONE_NOT_LOADED_MSG };
      raw = DEFAULT_TZ;
    }
    if (!_validZone(raw)) return { ok: false, code: 'zone', error: ZONE_BAD_MSG, tz: raw };
    return { ok: true, tz: raw };
  }

  function zone(opts) { const z = resolveZone(opts); return z.ok ? z.tz : null; }
  function setZone(tz) { _override = tz ? String(tz).trim() : null; }

  // TASK-1507 — one formatter per zone: payroll buckets every clock entry
  // through wallParts, and building an Intl.DateTimeFormat per call is slow.
  const _fmts = new Map();
  function _fmt(kind, tz) {
    const k = kind + '|' + tz;
    let f = _fmts.get(k);
    if (!f) {
      f = kind === 'off'
        ? new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
        : new Intl.DateTimeFormat('en-CA', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
      if (_fmts.size > 50) _fmts.clear();
      _fmts.set(k, f);
    }
    return f;
  }

  // Offset (ms) of `tz` from UTC at instant `ms`. Throws on an unknown zone.
  function _offsetMs(ms, tz) {
    const f = _fmt('off', tz);
    const p = {};
    f.formatToParts(new Date(ms)).forEach((x) => { p[x.type] = x.value; });
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    return asUtc - Math.floor(ms / 1000) * 1000;
  }

  // Wall-clock parts of instant `ms` in `tz`, or null.
  function wallParts(ms, tz) {
    if (!Number.isFinite(ms)) return null;
    try {
      const f = _fmt('wall', tz);
      const p = {};
      f.formatToParts(new Date(ms)).forEach((x) => { p[x.type] = x.value; });
      return { ymd: `${p.year}-${p.month}-${p.day}`, hh: (+p.hour) % 24, mm: +p.minute };
    } catch (_) { return null; }
  }

  function wallMatches(ms, ymd, hh, mm, tz) {
    const p = wallParts(ms, tz);
    return !!p && p.ymd === ymd && p.hh === hh && p.mm === mm;
  }

  // Every instant whose wall clock in `tz` reads ymd HH:MM, earliest first: one
  // on a normal day, NONE in a spring-forward gap, TWO in the repeated
  // fall-back hour (America/Chicago 2026-11-01 1:30 AM = 06:30Z CDT and 07:30Z
  // CST). null = unknown zone (refuse — never fall back to the device zone).
  function candidates(ymd, hh, mm, tz) {
    const [y, m, d] = String(ymd).split('-').map(Number);
    if (!y || !m || !d || !Number.isFinite(hh) || !Number.isFinite(mm)) return [];
    if (!_validZone(tz)) return null;
    const guess = Date.UTC(y, m - 1, d, hh, mm);
    try {
      const offs = new Set([-86400000, 0, 86400000].map((k) => _offsetMs(guess + k, tz)));
      const out = [];
      offs.forEach((off) => {
        const ms = guess - off;
        if (wallMatches(ms, ymd, hh, mm, tz) && out.indexOf(ms) === -1) out.push(ms);
      });
      return out.sort((a, b) => a - b);
    } catch (_) {
      return null;
    }
  }

  // "2026-11-01T01:30" → { ymd, hh, mm } | null
  function parseLocal(str) {
    const m = LOCAL_RE.exec(String(str == null ? '' : str).trim());
    if (!m) return null;
    const hh = Number(m[4]); const mm = Number(m[5]);
    if (hh > 23 || mm > 59) return null;
    return { ymd: `${m[1]}-${m[2]}-${m[3]}`, hh, mm };
  }

  function _pad(n) { return String(n).padStart(2, '0'); }
  function _key(key, str) { return String(key || '') + '|' + String(str || '').slice(0, 16); }

  function _toMs(v) {
    if (v == null || v === '') return NaN;
    if (typeof v === 'number') return v;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'object' && typeof v.seconds === 'number') return v.seconds * 1000;
    if (typeof v === 'object' && typeof v._seconds === 'number') return v._seconds * 1000;
    return new Date(v).getTime();
  }

  function remember(str, ms, key, kind) {
    if (!key || !str || !Number.isFinite(ms)) return;
    if (_hints.size > 200) _hints.clear();
    _hints.set(String(key), { str: String(str).slice(0, 16), ms, kind: kind || 'pick', at: Date.now() });
  }
  // The slot for `key` if it holds exactly this wall string and is fresh.
  function _slot(str, key) {
    if (!key) return null;
    const h = _hints.get(String(key));
    if (!h) return null;
    if (Date.now() - h.at > HINT_TTL_MS) { _hints.delete(String(key)); return null; }
    return h.str === String(str).slice(0, 16) ? h : null;
  }
  function clearHints() { _hints.clear(); }

  // Row-owned memory.
  function _rowRec(row, field) {
    if (!row || typeof row !== 'object' || !field) return null;
    const r = _rows.get(row);
    return r && r[field] ? r[field] : null;
  }
  function _rowSet(row, field, str, ms) {
    if (!row || typeof row !== 'object' || !field) return;
    const r = _rows.get(row) || {};
    r[field] = { str: String(str).slice(0, 16), ms };
    _rows.set(row, r);
  }
  // Set row[field] from a KNOWN instant (stored line, the resolved clock pair)
  // and remember that instant on the row, so the row re-reads as exactly it.
  function stampRow(row, field, instant, opts) {
    const ms = _toMs(instant);
    const str = formatLocal(ms, { tz: opts && opts.tz });
    if (row && field) {
      row[field] = str;
      if (str && Number.isFinite(ms)) _rowSet(row, field, str, Math.floor(ms / 60000) * 60000);
    }
    return str;
  }

  // Stored instant → "YYYY-MM-DDTHH:MM" in the company zone ('' for no time).
  // Unknown zone: formats in the device zone so the box is not blank — the
  // save path refuses on that same unknown zone, so nothing wrong is written.
  function formatLocal(instant, opts) {
    const ms = _toMs(instant);
    if (!Number.isFinite(ms)) {
      // TASK-9971 (COD r1 independent): filling a keyed box with NO time must
      // forget the slot, or a stored repeated-hour instant from the LAST entry
      // opened in that box silently answers a 1:30 typed on this one.
      if (opts && opts.key && !opts.noRemember) _hints.delete(String(opts.key));
      return '';
    }
    const z = resolveZone(opts);
    if (!z.ok) {
      const d = new Date(ms);
      return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
    }
    const p = wallParts(ms, z.tz);
    if (!p) return '';
    const str = `${p.ymd}T${_pad(p.hh)}:${_pad(p.mm)}`;
    const c = candidates(p.ymd, p.hh, p.mm, z.tz);
    if (opts && opts.key && !opts.noRemember) {
      // The slot is overwritten on EVERY fill (not only for repeated-hour
      // values), so an old fill can never answer for a different entry.
      // Minute-truncate: the box shows minutes, the stored instant may carry seconds.
      const m0 = Math.floor(ms / 60000) * 60000;
      const pick = c && c.length ? (c.find((x) => Math.abs(x - m0) < 60000)) : null;
      remember(str, pick != null ? pick : m0, opts.key, 'render');
    }
    return str;
  }

  // Labels / day keys only — never fill a box, so they never leave a hint.
  function dateKey(instant, opts) { return formatLocal(instant, { ...(opts || {}), noRemember: true }).slice(0, 10); }
  function timeHM(instant, opts) { return formatLocal(instant, { ...(opts || {}), noRemember: true }).slice(11, 16); }
  function todayKey(opts) { return dateKey(Date.now(), opts); }

  function zoneLabel(ms, opts) {
    const z = resolveZone(opts);
    try {
      return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: z.ok ? z.tz : undefined, timeZoneName: 'short' });
    } catch (_) { return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
  }

  function _niceWall(p) {
    const h12 = ((p.hh + 11) % 12) + 1;
    const ap = p.hh < 12 ? 'AM' : 'PM';
    let day = p.ymd;
    try { day = new Date(p.ymd + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }); } catch (_) { /* keep ymd */ }
    return `${h12}:${_pad(p.mm)} ${ap} on ${day}`;
  }

  // Wall string → instant.
  //   { ok:true, ms }
  //   { ok:false, code:'empty'|'invalid'|'zone'|'not-loaded'|'gap'|'ambiguous', error, choices? }
  // opts: { tz, key, which:'first'|'second', label }
  function read(str, opts) {
    const s = String(str == null ? '' : str).trim();
    const label = (opts && opts.label) ? opts.label + ': ' : '';
    if (!s) return { ok: false, code: 'empty', error: label + 'Enter a date and time.' };
    const p = parseLocal(s);
    if (!p) return { ok: false, code: 'invalid', error: label + 'That date/time could not be read.' };
    const z = resolveZone(opts);
    if (!z.ok) return { ok: false, code: z.code, error: z.error };
    const c = candidates(p.ymd, p.hh, p.mm, z.tz);
    if (!c) return { ok: false, code: 'zone', error: ZONE_BAD_MSG };
    if (!c.length) return { ok: false, code: 'gap', error: `${label}${_niceWall(p)} doesn't exist — the clocks jumped forward an hour that night. Pick a time just after it.` };
    if (c.length === 1) return { ok: true, ms: c[0] };
    const which = opts && opts.which;
    if (which === 'first') return { ok: true, ms: c[0] };
    if (which === 'second') return { ok: true, ms: c[c.length - 1] };
    // noHints: a non-interactive caller (e.g. a generator) must not inherit a
    // pick or a filled box — an ambiguous time stays ambiguous for it.
    if (!(opts && opts.noHints)) {
      if (opts && opts.row) {
        const rr = _rowRec(opts.row, opts.field);
        if (rr && rr.str === s.slice(0, 16) && c.indexOf(rr.ms) !== -1) return { ok: true, ms: rr.ms };
      } else {
        const h = _slot(s, opts && opts.key);
        const usable = h && (h.kind === 'render' || !(opts && opts.picksOnlyIfReuse) || opts.reusePicks);
        if (usable && c.indexOf(h.ms) !== -1) return { ok: true, ms: h.ms };
      }
    }
    const choices = [
      // COD UX: plain words next to the zone abbreviation (hours come from describe).
      { which: 'first', ms: c[0], label: zoneLabel(c[0], { tz: z.tz }) + ' (first, before the clocks went back)' },
      { which: 'second', ms: c[c.length - 1], label: zoneLabel(c[c.length - 1], { tz: z.tz }) + ' (second, after the clocks went back)' },
    ];
    return { ok: false, code: 'ambiguous', choices, error: `${label}The clocks fell back that night, so ${_niceWall(p)} happened twice. Pick which one.` };
  }

  // Strict: NaN unless read() resolves (an unresolved repeated-hour time is NaN —
  // Timestamp.fromDate(new Date(NaN)) throws, so a save that skipped resolveAll
  // fails loudly instead of guessing).
  function toMs(str, opts) { const r = read(str, opts); return r.ok ? r.ms : NaN; }
  function toDate(str, opts) { return new Date(toMs(str, opts)); }

  // For inline previews / red borders only: the resolved instant, or for an
  // unresolved repeated-hour time the FIRST one (flagged by read()). Never save it.
  function peekMs(str, opts) {
    const r = read(str, opts);
    if (r.ok) return r.ms;
    if (r.code === 'ambiguous') return r.choices[0].ms;
    return NaN;
  }

  function hm(hours) {
    const mins = Math.round(hours * 60);
    return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
  }

  // Hours between two wall strings, for a preview that matches what is saved.
  //   { ok:true, hours, startMs, endMs }
  //   { ok:false, ambiguous:true, options:[{ hours, text }] }  — show both
  //   { ok:false, error }
  function hoursBetween(startStr, endStr, opts) {
    const o = opts || {};
    const a = read(startStr, o.row ? { ...o, field: 'startTime' } : { ...o, key: o.startKey != null ? o.startKey : o.key });
    const b = read(endStr, o.row ? { ...o, field: 'endTime' } : { ...o, key: o.endKey != null ? o.endKey : o.key });
    for (const r of [a, b]) if (!r.ok && r.code !== 'ambiguous') return { ok: false, error: r.error, code: r.code };
    if (a.ok && b.ok) return { ok: true, hours: (b.ms - a.ms) / 3600000, startMs: a.ms, endMs: b.ms };
    const as = a.ok ? [{ ms: a.ms, label: '' }] : a.choices;
    const bs = b.ok ? [{ ms: b.ms, label: '' }] : b.choices;
    const options = [];
    as.forEach((x) => bs.forEach((y) => {
      const hours = (y.ms - x.ms) / 3600000;
      if (hours < 0) return;
      const which = [a.ok ? '' : 'start ' + x.label, b.ok ? '' : 'end ' + y.label].filter(Boolean).join(', ');
      options.push({ hours, text: `${hm(hours)} (${which})` });
    }));
    return { ok: false, ambiguous: true, options, error: 'That time happened twice (clocks fell back). You will pick which one when you save.' };
  }

  // A short preview line for an ambiguous pair, e.g. "7h 30m or 8h 30m — pick on save".
  function ambiguousPreviewText(hb) {
    if (!hb || !hb.ambiguous || !hb.options || !hb.options.length) return '';
    return hb.options.map((o) => hm(o.hours)).join(' or ') + ' — the clocks fell back that night; you will pick which time when you save.';
  }

  function _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Resolve every wall string a save is about to write. Refuses (no prompt) on
  // an unreadable zone, a nonexistent time, or an unreadable value; asks ONE
  // dialog for every repeated-hour time not already resolved by a hint/choice.
  //   items: [{ str, key?, label? , optional? }]   (empty str: skipped when optional, else refused)
  //   opts:  { tz, describe(item, ms) -> string (e.g. ' — 8h 30m shift'), title }
  // → { ok:true, ms:[...per item, NaN for skipped] } | { ok:false, message, cancelled? }
  //   items: [{ str, key?, row?, field?, label?, optional? }]
  //     key        — a single field (its own slot); row+field — a ticket row time
  //   opts: { tz, describe, title,
  //           reusePicks — this is the SAME save as an earlier resolveAll (a nested
  //                        save): a pick already made for a keyed field counts,
  //           noPrompt   — never open the dialog; an unresolved repeated time is
  //                        refused (code 'ambiguous') }
  // Every repeated-hour item is its own question (COD 1.15.2009): a row from the
  // first 1:30 to the second 1:30 is enterable, and nothing is inherited from
  // another row, another field, or an earlier save's pick.
  async function resolveAll(items, opts) {
    const o = opts || {};
    const list = (items || []).map((it) => (typeof it === 'string' ? { str: it } : (it || {})));
    const out = new Array(list.length).fill(NaN);
    const ask = [];
    for (let i = 0; i < list.length; i++) {
      const it = list[i];
      const s = String(it.str == null ? '' : it.str).trim();
      if (!s) {
        if (it.optional !== false) continue;
        return { ok: false, message: (it.label ? it.label + ': ' : '') + 'Enter a date and time.' };
      }
      const r = read(s, { tz: o.tz, key: it.key, row: it.row, field: it.field, label: it.label, picksOnlyIfReuse: true, reusePicks: !!o.reusePicks });
      if (r.ok) { out[i] = r.ms; continue; }
      if (r.code !== 'ambiguous') return { ok: false, message: r.error, code: r.code, item: i };
      ask.push({ i, it, s, r });
    }
    if (!ask.length) return { ok: true, ms: out };
    if (o.noPrompt) return { ok: false, code: 'ambiguous', message: ask[0].r.error, item: ask[0].i };
    const picked = await _pickRepeated(ask, o);
    if (!picked) return { ok: false, cancelled: true, message: 'Not saved — pick which time you meant.' };
    ask.forEach((a, n) => {
      const c = picked[n] === 'second' ? a.r.choices[1] : a.r.choices[0];
      out[a.i] = c.ms;
      if (a.it.row) _rowSet(a.it.row, a.it.field, a.s, c.ms);
      else remember(a.s, c.ms, a.it.key, 'pick');
    });
    return { ok: true, ms: out };
  }

  // A ticket row's time as an instant (strict; NaN when unresolved), its peek
  // for previews, and its hours text / number.
  function rowMs(row, field, opts) { const r = read(row && row[field], { ...(opts || {}), row, field }); return r.ok ? r.ms : NaN; }
  function rowDate(row, field, opts) { return new Date(rowMs(row, field, opts)); }
  function rowPeekMs(row, field, opts) {
    const r = read(row && row[field], { ...(opts || {}), row, field });
    if (r.ok) return r.ms;
    return r.code === 'ambiguous' ? r.choices[0].ms : NaN;
  }

  // The dialog: one radio pair per repeated-hour time, hours shown per choice.
  // Resolves ['first'|'second', ...] or null on Cancel. Nothing is pre-checked.
  // Test seam: window.__TLT_AUTOPICK = 'first'|'second' answers without a dialog
  // (Node unit tests; never set in the app).
  function _pickRepeated(ask, o) {
    const auto = root && root.__TLT_AUTOPICK;
    if (auto && root) root.__TLT_LAST_ASK = ask.length; // test seam: questions in this dialog
    if (auto === 'first' || auto === 'second') return Promise.resolve(ask.map(() => auto));
    const seq = root && root.__TLT_AUTOPICK_SEQ; // test seam: one answer per question, in order
    if (Array.isArray(seq)) { if (root) root.__TLT_LAST_ASK = ask.length; return Promise.resolve(ask.map((_, i) => seq[i] || 'first')); }
    if (auto === 'cancel') return Promise.resolve(null);
    const doc = root && root.document;
    if (!doc || !doc.body) return Promise.resolve(null);
    // A picker already open (a second save tap) is cancelled, never orphaned:
    // its caller gets null and writes nothing.
    const prev = doc.getElementById('tlt-repeated-hour');
    if (prev) { try { prev.__tltCancel && prev.__tltCancel(); } catch (_) { /* gone */ } prev.remove(); }
    return new Promise((resolve) => {
      const modal = doc.createElement('div');
      modal.className = 'modal';
      modal.id = 'tlt-repeated-hour';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'tlt-repeated-hour-title');
      modal.style.zIndex = '10060';
      modal.style.display = 'flex';
      const groups = ask.map((a, n) => {
        const p = parseLocal(a.s);
        const head = (a.it.label ? _esc(a.it.label) + ': ' : '') + _esc(_niceWall(p));
        const rows = a.r.choices.map((c) => {
          let extra = '';
          try { extra = o.describe ? String(o.describe(a.it, c.ms) || '') : ''; } catch (_) { extra = ''; }
          return `<label style="display:flex;align-items:center;gap:8px;min-height:44px;padding:4px 8px;border:1px solid var(--border-light,#CBD5E1);border-radius:8px;margin-bottom:6px;cursor:pointer;color:var(--text-primary,#0F172A);background:#fff;">
              <input type="radio" name="tlt-rep-${n}" value="${c.which}"> <span>${_esc(c.label)}${_esc(extra)}</span></label>`;
        }).join('');
        return `<fieldset data-tlt-group="${n}" style="border:0;margin:0 0 10px;padding:0;text-align:left;">
            <legend style="font-size:0.85rem;font-weight:600;color:var(--text-primary,#0F172A);margin-bottom:6px;">${head}</legend>${rows}</fieldset>`;
      }).join('');
      modal.innerHTML = `
        <div class="modal-content modal-small" style="max-width:440px;width:calc(100% - 32px);margin:auto;">
          <div class="modal-body" style="padding:20px 16px 16px;">
            <div style="text-align:center;">
              <span class="material-icons" style="font-size:40px;color:#B45309;">schedule</span>
              <h3 id="tlt-repeated-hour-title" style="margin:6px 0 6px;font-size:1.1rem;color:var(--text-primary,#0F172A);">${_esc(o.title || 'Which time did you mean?')}</h3>
              <p style="margin:0 0 12px;font-size:0.88rem;line-height:1.4;color:var(--text-secondary,#475569);">The clocks fell back an hour that night, so this time happened twice. Pick the one you meant — the hours change by one.</p>
            </div>
            ${groups}
            <div data-tlt-error role="alert" style="min-height:1.2em;margin:4px 0 8px;font-size:0.85rem;font-weight:600;color:#B91C1C;"></div>
            <button type="button" class="btn btn-primary" data-tlt-ok="1" style="width:100%;min-height:48px;font-weight:700;margin-bottom:8px;">Use these times</button>
            <button type="button" class="btn btn-secondary" data-tlt-cancel="1" style="width:100%;min-height:44px;">Cancel</button>
          </div>
        </div>`;
      let settled = false;
      const done = (v) => { if (settled) return; settled = true; modal.remove(); resolve(v); };
      modal.__tltCancel = () => done(null);
      modal.addEventListener('click', (ev) => {
        if (ev.target === modal) return done(null);
        const btn = ev.target && ev.target.closest ? ev.target.closest('button') : null;
        if (!btn) return;
        if (btn.hasAttribute('data-tlt-cancel')) return done(null);
        if (!btn.hasAttribute('data-tlt-ok')) return;
        const picks = ask.map((_, n) => {
          const el = modal.querySelector(`input[name="tlt-rep-${n}"]:checked`);
          return el ? el.value : null;
        });
        if (picks.some((x) => !x)) {
          const er = modal.querySelector('[data-tlt-error]');
          if (er) er.textContent = 'Pick one time for each.';
          return;
        }
        done(picks);
      });
      doc.body.appendChild(modal);
    });
  }

  // ── TASK-1507 / TASK-9979 — the company WORKWEEK, one helper for all ──────
  // Payroll (payRates.js), Clock (computeCrossPeriodOT, timesheets) and BI
  // bucket hours into workweeks through THESE functions, so the three can never
  // disagree. A workweek starts at 12:00 AM on the company's workweek start day
  // (settings.workWeekStartDay — TITS = Monday; the CALLER passes it, nothing is
  // hardcoded here) in the COMPANY zone (settings.timezone), whatever zone the
  // viewer's phone is in. Days are counted on the calendar, never as 24 h
  // blocks, so the fall-back week (25 h day) and spring-forward week (23 h day)
  // bucket exactly like any other.
  // Zone: the same resolution as read()/formatLocal(); unset → America/Chicago
  // (as the server does). Unlike read(), week math never REFUSES — a payroll
  // total has to render — so an unloaded or unreadable zone uses the default.
  // Week KEY = the start day's company date, 'YYYY-MM-DD'.
  function weekZone(opts) {
    const z = resolveZone({ ...(opts || {}), allowDefault: true });
    return z.ok ? z.tz : DEFAULT_TZ;
  }
  // TASK-9878 — the workweek math itself lives in ONE place:
  // functions/src/shared/payPeriodHours.js (window.PayPeriodHours, generated by
  // scripts/build-shared.cjs), the same file the Claude connector's
  // get_payroll_hours runs on the server. These helpers keep their signatures
  // and resolve the zone exactly as before (weekZone), then delegate. Read at
  // CALL time; in Node (unit tests) the generated mirror is require()d.
  // A missing mirror THROWS — never a local fallback (that is a second copy).
  function _pph() {
    const P = root && root.PayPeriodHours;
    if (P && typeof P.weekKey === 'function') return P;
    try {
      if (typeof require === 'function') {
        const m = require('../shared/pay-period-hours.js');
        if (m && typeof m.weekKey === 'function') return m;
      }
    } catch (_) { /* browser: window.PayPeriodHours only */ }
    throw new Error('Pay-period rule not loaded — reload the page.');
  }
  // Calendar arithmetic on 'YYYY-MM-DD' (no zone, no DST).
  function addDaysYmd(ymd, n) { return _pph().addDaysYmd(ymd, n); }
  // The company calendar date an instant falls on.
  function companyYmd(instant, opts) { return _pph().companyYmd(_toMs(instant), weekZone(opts)); }
  // 12:00 AM of a company date, as an instant. A zone whose clocks skip
  // midnight starts the day at the first minute that exists.
  function dayStartMs(ymd, opts) { return _pph().dayStartMs(ymd, weekZone(opts)); }
  // Week key (start day's company date) of an instant.
  function weekKey(instant, startDay, opts) { return _pph().weekKey(_toMs(instant), startDay, weekZone(opts)); }
  // { key, startMs, endMs, lastYmd } — endMs = the last millisecond before the
  // next week starts (a fall-back week is 169 h long, a spring-forward week 167 h).
  function weekRangeOfKey(key, opts) { return _pph().weekRangeOfKey(key, weekZone(opts)); }
  function weekRange(instant, startDay, opts) { return _pph().weekRange(_toMs(instant), startDay, weekZone(opts)); }
  // 0..6 — which day of its workweek an instant falls on (company calendar).
  function weekDayIndex(instant, startDay, opts) { return _pph().weekDayIndex(_toMs(instant), startDay, weekZone(opts)); }
  // A Date used as a CALENDAR DAY (pay-period bounds, pickers: midnight on the
  // device) → its 'YYYY-MM-DD' as the device reads it.
  function carrierYmd(d) {
    const x = d instanceof Date ? d : new Date(_toMs(d));
    if (isNaN(x.getTime())) return null;
    return `${x.getFullYear()}-${_pad(x.getMonth() + 1)}-${_pad(x.getDate())}`;
  }
  // 'YYYY-MM-DD' → that calendar day as a device-midnight Date (the carrier the
  // views format and step with setDate). Never compare it with an instant.
  function ymdCarrier(ymd) { const [y, m, d] = String(ymd).split('-').map(Number); return new Date(y, m - 1, d); }
  // A pay period given as calendar-day carriers → its company-zone instants:
  // { startMs: 12:00 AM of the first day, endMs: the last ms of the last day }.
  // For a viewer in the company zone this equals the carriers' own times.
  function periodBoundsMs(startDay, endDay, opts) {
    const a = carrierYmd(startDay); const b = carrierYmd(endDay);
    if (!a || !b) return { startMs: NaN, endMs: NaN };
    return _pph().periodBoundsYmd(a, b, weekZone(opts));
  }

  const api = {
    weekZone, addDaysYmd, companyYmd, dayStartMs, weekKey, weekRange, weekRangeOfKey,
    weekDayIndex, carrierYmd, ymdCarrier, periodBoundsMs,
    DEFAULT_TZ, HINT_TTL_MS, ZONE_BAD_MSG, ZONE_NOT_LOADED_MSG,
    resolveZone, zone, setZone, candidates, wallParts, wallMatches, parseLocal,
    formatLocal, dateKey, timeHM, todayKey, zoneLabel, read, toMs, toDate, peekMs,
    hoursBetween, ambiguousPreviewText, hm, resolveAll, remember, clearHints,
    stampRow, rowMs, rowDate, rowPeekMs,
  };
  if (root) root.TenantLocalTime = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
